// apps/app/src/features/chat/Messages/Item/useMessageReactions.ts
import type { MessageReaction } from '@hvf/contracts'
import { userId } from '@stateApp'
import { createEffect, createSignal } from 'solid-js'
import { useService } from 'solid-services'
import { useUpsert } from '../../state'

type Reaction = Pick<MessageReaction, 'emoji' | 'created_by'>

export function useMessageReactions(messageId: string, initialReactions?: Reaction[]) {
  const { reactionMutation } = useService(useUpsert)()
  const [localReactions, setLocalReactions] = createSignal<Reaction[]>(initialReactions || [])

  // SMART SYNC: Prevent cache updates from clobbering optimistic state
  createEffect((prevHash) => {
    const serverReactions = initialReactions || []

    const currentHash = serverReactions
      .map((r) => `${r.emoji}:${r.created_by}`)
      .sort()
      .join('|')

    if (currentHash !== prevHash) {
      setLocalReactions(serverReactions)
    }

    return currentHash
  }, "")

  const handleReaction = (emoji: string) => {
    const currentUserId = userId()!

    // Optimistically update local state
    setLocalReactions((prev) => {
      // Check if the user already has this exact reaction active
      const hasSameReaction = prev.some(
        (r) => r.emoji === emoji && r.created_by === currentUserId
      )

      // Filter out ANY reaction previously made by this user on this message
      const filtered = prev.filter((r) => r.created_by !== currentUserId)

      // If they clicked the same emoji, toggle it off completely
      if (hasSameReaction) {
        return filtered
      }

      // If it's a different emoji (or their first one), replace/add it
      return [...filtered, { emoji, created_by: currentUserId }]
    })

    // Fire mutation with rollback
    reactionMutation.mutate(
      {
        emoji,
        message_id: messageId,
        created_by: currentUserId,
      },
      {
        onError: () => {
          setLocalReactions(initialReactions || [])
        },
      }
    )
  }

  return { localReactions, handleReaction }
}
